import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  FacebookShareButton,
  TwitterShareButton,
  PinterestShareButton,
} from 'react-share';
import { shareWithFriend } from '../../redux/actions';
import { Button } from '../../components';

import { ShareSection, ShareHeading, ShareList, ShareButton, ShareInputContainer, ShareInput, ShareMessage } from './styles';

const EmailSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required('Email is required'),
});

const originUrl = typeof window !== 'undefined' && window.location.origin || 'https://dayaxe.com';

const ShareBooking = ({ bookingId, shareFriend, handleFormSubmit, hotelName, neighborhood, productName, photo, productUrl }) => (
  <ShareSection>
    <ShareHeading>Share with your friends</ShareHeading>
    <Formik
      initialValues={{
        email: '',
      }}
      validationSchema={EmailSchema}
      onSubmit={values => handleFormSubmit(values.email, bookingId)}
    >
      {({ errors, touched, handleBlur, handleChange, values }) => (
        <Form>
          <ShareInputContainer>
            <ShareInput
              name="email"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              hasError={errors.email && touched.email}
              errors={errors.email}
              placeholder="E-mail"
            />
            {errors.email && touched.email && <ShareMessage status='failure'>{errors.email}</ShareMessage>}
            {shareFriend.status === 'failure' && <ShareMessage status='failure'>{shareFriend.error}</ShareMessage>}
            {shareFriend.status === 'success' && <ShareMessage status='success'>Success. Maybe share with someone else?</ShareMessage>}
            <Button htmlType="submit" theme="primary">Share</Button>
          </ShareInputContainer>
        </Form>
      )}
    </Formik>
    <ShareList>
      <li>
        <FacebookShareButton url={`${originUrl}${productUrl}`} quote={`Just booked a ${productName} at ${hotelName} ${neighborhood} via @dayaxeapp. Join me #poolside for some fun in the sun! #dayaxe #minivacay`} hashtag="#dayaxe #minivacay">
          <ShareButton>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="16">
              <path fillRule="evenodd" d="M9.085.003L6.926 0C4.501 0 2.934 1.546 2.934 3.938v1.815H.764a.333.333 0 0 0-.34.327v2.63c0 .18.152.326.34.326h2.17v6.638c0 .18.152.326.34.326h2.831c.188 0 .34-.146.34-.326V9.036h2.537c.188 0 .34-.146.34-.326V6.08a.32.32 0 0 0-.099-.231.347.347 0 0 0-.24-.096H6.444V4.214c0-.74.184-1.115 1.186-1.115h1.455c.187 0 .339-.147.339-.327V.33a.333.333 0 0 0-.34-.327z" clipRule="evenodd" />
            </svg>
          </ShareButton>
        </FacebookShareButton>
      </li>
      <li>
        <TwitterShareButton title={`Just booked a ${productName} at ${hotelName} via @dayaxeapp. Join me #poolside for some fun in the sun!`} hashtags={['dayaxe',  'minivacay']} url="dayaxe.com">
          <ShareButton>
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="13">
              <path fillRule="evenodd" d="M15 1.54a6.1 6.1 0 0 1-1.77.52A3.23 3.23 0 0 0 14.58.24c-.6.38-1.25.65-1.95.8A3 3 0 0 0 10.38 0c-1.7 0-3.07 1.47-3.07 3.28 0 .26.02.51.07.75A8.54 8.54 0 0 1 1.04.6C.78 1.09.62 1.65.62 2.26c0 1.13.55 2.14 1.37 2.72a2.9 2.9 0 0 1-1.39-.4v.03c0 1.6 1.07 2.92 2.46 3.23a2.89 2.89 0 0 1-1.38.05 3.14 3.14 0 0 0 2.87 2.28A5.94 5.94 0 0 1 0 11.53 8.27 8.27 0 0 0 4.72 13c5.66 0 8.75-5 8.75-9.33l-.01-.43A6.37 6.37 0 0 0 15 1.54z" clipRule="evenodd" />
            </svg>
          </ShareButton>
        </TwitterShareButton>
      </li>
      {photo && (
        <li>
          <PinterestShareButton media={`${process.env.CDN_URL}${photo}`} url={`${originUrl}${productUrl}`} description={`Just booked a ${productName} at ${hotelName} via @dayaxeapp. Join me #poolside for some fun in the sun! #dayaxe #minivacay`}>
            <ShareButton>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="17">
                <path fillRule="evenodd" d="M12.92 4.73C12.47 1.37 9.18-.34 5.66.06 2.9.37.12 2.66.01 5.92c-.07 2 .48 3.5 2.35 3.91.8-1.45-.26-1.77-.43-2.82-.69-4.3 4.88-7.24 7.8-4.24 2.01 2.08.68 8.49-2.57 7.82-3.11-.63 1.52-5.73-.96-6.73-2.02-.81-3.1 2.49-2.13 4.13C3.5 10.8 2.29 13.46 2.78 17c1.6-1.17 2.13-3.42 2.57-5.76.8.5 1.22 1 2.24 1.09 3.75.3 5.85-3.81 5.33-7.6z" clipRule="evenodd" />
              </svg>
            </ShareButton>
          </PinterestShareButton>
        </li>
      )}
    </ShareList>
  </ShareSection>
);

ShareBooking.propTypes = {
  bookingId: PropTypes.number,
  shareFriend: PropTypes.instanceOf(Object),
  handleFormSubmit: PropTypes.func,
  hotelName: PropTypes.string,
  neighborhood: PropTypes.string,
  productName: PropTypes.string,
  productUrl: PropTypes.string,
  photo: PropTypes.string,
};

const mapStateToProps = state => ({
  shareFriend: state.shareWithFriend,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  handleFormSubmit: shareWithFriend,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ShareBooking);
